import { useMemo } from 'react';
import PropTypes from 'prop-types';

import styles from './MobileApprovalLine.module.scss';

/**
 * 모바일 결재선 컴포넌트
 * - 결재자 목록을 카드 형태로 표시
 * - 결재 상태에 따른 스타일 적용
 */
function MobileApprovalLine({ documentInfo }) {
  // 결재자 목록 구성
  const approvers = useMemo(
    function () {
      if (!documentInfo?.approvalLine) {
        return [];
      }

      const line = documentInfo.approvalLine;
      const result = [];

      // 기안자 추가
      if (line.drafter) {
        result.push({
          type: 'drafter',
          typeLabel: '기안',
          name: line.drafter.name || '-',
          position: line.drafter.position || '',
          team: line.drafter.team || '',
          state: 3, // 기안자는 항상 완료
        });
      }

      // 검토자 추가
      const checkers = ['checker1', 'checker2', 'checker3', 'checker4'];
      checkers.forEach(function (key, index) {
        if (line[key] && line[key].enumber) {
          result.push({
            type: 'checker',
            typeLabel: '검토',
            name: line[key].name || '-',
            position: line[key].position || '',
            team: line[key].team || '',
            state: line[key].state || 1,
            order: index + 1,
          });
        }
      });

      // 합의자 추가
      if (line.agreement && line.agreement.enumber) {
        result.push({
          type: 'agreement',
          typeLabel: '합의',
          name: line.agreement.name || '-',
          position: line.agreement.position || '',
          team: line.agreement.team || '',
          state: line.agreement.state || 1,
        });
      }

      // 결재자 추가
      const signers = ['signer2', 'signer3', 'signer4', 'signer5'];
      signers.forEach(function (key, index) {
        if (line[key] && line[key].enumber) {
          result.push({
            type: 'signer',
            typeLabel: '결재',
            name: line[key].name || '-',
            position: line[key].position || '',
            team: line[key].team || '',
            state: line[key].state || 1,
            order: index + 1,
          });
        }
      });

      return result;
    },
    [documentInfo?.approvalLine],
  );

  if (approvers.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <h3 className={styles.sectionTitle}>결재선</h3>
      <div className={styles.approverList}>
        {approvers.map(function (approver, index) {
          return (
            <div key={`approver-${index}`} className={styles.approverCard}>
              <div className={styles.approverType}>
                <span className={`${styles.typeLabel} ${getTypeClass(approver.type)}`}>
                  {approver.typeLabel}
                </span>
              </div>
              <div className={styles.approverInfo}>
                <span className={styles.approverName}>{approver.name}</span>
                <span className={styles.approverPosition}>
                  {approver.position}
                  {approver.team && ` / ${approver.team}`}
                </span>
              </div>
              <div className={styles.approverStatus}>
                <span className={`${styles.statusBadge} ${getStateClass(approver.state)}`}>
                  {getStateText(approver.state)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 결재자 유형에 따른 스타일 클래스
 */
function getTypeClass(type) {
  switch (type) {
    case 'drafter':
      return styles.typeDrafter;
    case 'checker':
      return styles.typeChecker;
    case 'agreement':
      return styles.typeAgreement;
    case 'signer':
      return styles.typeSigner;
    default:
      return '';
  }
}

/**
 * 결재 상태에 따른 스타일 클래스
 */
function getStateClass(state) {
  switch (state) {
    case 1:
      return styles.statePending;
    case 2:
      return styles.stateWaiting;
    case 3:
      return styles.stateApproved;
    case 4: // 반려
    case 6: // 수정요청
      return styles.stateRejected;
    default:
      return styles.statePending;
  }
}

/**
 * 결재 상태 텍스트
 */
function getStateText(state) {
  switch (state) {
    case 1:
      return '대기';
    case 2:
      return '진행';
    case 3:
      return '완료';
    case 4:
      return '반려';
    case 6:
      return '수정요청';
    default:
      return '대기';
  }
}

MobileApprovalLine.propTypes = {
  documentInfo: PropTypes.shape({
    approvalLine: PropTypes.object,
  }),
  docIdx: PropTypes.number,
  tabType: PropTypes.string,
};

MobileApprovalLine.defaultProps = {
  documentInfo: {},
  docIdx: null,
  tabType: '',
};

export default MobileApprovalLine;
